import {
  IFieldDefinition,
  LayoutElement,
  isFieldElement,
  isInputFieldElement,
  isLayoutTableFieldElement,
} from "@entaconsulting/form";
import { Chip, Divider, Stack, TextField } from "@mui/material";
import EntaAccordion from "common/components/EntaAccordion";
import { PanelSections } from "developer/edit/constants";
import useDynamicField from "developer/edit/hooks/useDynamicField";
import useIsExpanded from "developer/edit/hooks/useIsExpanded";
import { isNumber } from "lodash";
import EditorVisualTextField from "../EditorVisualTextField";
import DynamicField from "./DynamicField";
import BlacklistField from "./schemaFields/BlacklistField";
import LengthField from "./schemaFields/LengthField";
import MaxField from "./schemaFields/MaxField";
import MinField from "./schemaFields/MinField";
import MultipleFilesField from "./schemaFields/MultipleField";
import WhitelistField from "./schemaFields/WhitelistField";

const ValidationSection = () => {
  const { isExpanded, handleChangeIsExpanded, isDefaultExpanded, hide } =
    useIsExpanded(PanelSections.Validation);
  const {
    input: { value: element },
  } = useDynamicField<LayoutElement>("element");
  const {
    input: { value: fieldDef },
  } = useDynamicField<IFieldDefinition>("fieldDef");

  const isFieldEl = isFieldElement(element);
  const isInputFieldEl = isFieldEl && isInputFieldElement(element, fieldDef);
  const isTableFieldEl = isLayoutTableFieldElement(element);
  const isDateFieldEl =
    (isFieldEl && fieldDef.type === "date") || fieldDef.type === "datetime";
  const isAttachmentFieldEl = isFieldEl && fieldDef.type === "attachment";
  const isBooleanFieldEl = isFieldEl && fieldDef.type === "boolean";
  const isNumericFieldEl = isFieldEl && fieldDef.type === "number";
  const isEmailFieldEl = isFieldEl && fieldDef.type === "email";

  if (hide) return null;

  return (
    <EntaAccordion
      title="Validations"
      expanded={isExpanded}
      defaultExpanded={isDefaultExpanded}
      onChange={handleChangeIsExpanded}
    >
      <Stack spacing={1}>
        {isAttachmentFieldEl && (
          <>
            <Stack direction="row" spacing={2}>
              <DynamicField name="fieldDef.fileType">
                {(props) => (
                  <TextField
                    label="File Type"
                    onChange={props.input.onChange}
                    value={props.input.value}
                    name={props.input.name}
                    fullWidth
                  />
                )}
              </DynamicField>
              <DynamicField
                name="fieldDef.maxSizeMb"
                parse={(value) => (isNumber(value) ? value : Number(value))}
              >
                {(props) => (
                  <TextField
                    type="number"
                    label="Max Size Mb"
                    onChange={props.input.onChange}
                    value={props.input.value}
                    name={props.input.name}
                    fullWidth
                  />
                )}
              </DynamicField>
            </Stack>
            <Stack direction="row" spacing={2} width={1 / 2}>
              <DynamicField name="fieldDef.multiple">
                {(props) => (
                  <MultipleFilesField
                    onChange={props.input.onChange}
                    value={props.input.value}
                    name={props.input.name}
                  />
                )}
              </DynamicField>
            </Stack>
          </>
        )}
        <Stack direction="row" spacing={1}>
          {!isNumericFieldEl &&
            !isEmailFieldEl &&
            !isDateFieldEl &&
            !isTableFieldEl &&
            !isAttachmentFieldEl &&
            !isBooleanFieldEl && (
              <DynamicField name="fieldDef.length">
                {(props) => (
                  <LengthField
                    onChange={props.input.onChange}
                    value={props.input.value}
                    name={props.input.name}
                  />
                )}
              </DynamicField>
            )}
          {(isInputFieldEl || isTableFieldEl) && (
            <>
              <DynamicField name="fieldDef.min">
                {(props) => (
                  <MinField
                    onChange={props.input.onChange}
                    value={props.input.value}
                    name={props.input.name}
                  />
                )}
              </DynamicField>
              <DynamicField name="fieldDef.max">
                {(props) => (
                  <MaxField
                    onChange={props.input.onChange}
                    value={props.input.value}
                    name={props.input.name}
                  />
                )}
              </DynamicField>
            </>
          )}
        </Stack>
        {isInputFieldEl && (
          <>
            <DynamicField name="fieldDef.whitelist">
              {(props) => (
                <WhitelistField
                  name={props.input.name}
                  value={props.input.value}
                  onChange={props.input.onChange}
                />
              )}
            </DynamicField>
            <DynamicField name="fieldDef.blacklist">
              {(props) => (
                <BlacklistField
                  name={props.input.name}
                  value={props.input.value}
                  onChange={props.input.onChange}
                />
              )}
            </DynamicField>
            <Stack spacing={2} pt={1}>
              <Divider variant="fullWidth">
                <Chip label="RegExp Validation" size="small" />
              </Divider>
              <Stack spacing={1}>
                <DynamicField name="fieldDef.matches.regex">
                  {(props) => (
                    <EditorVisualTextField
                      label="Match RegExp"
                      fullWidth
                      name={props.input.name}
                      value={props.input.value}
                      onChange={props.input.onChange}
                    />
                  )}
                </DynamicField>
                <DynamicField name="fieldDef.matches.message">
                  {(props) => (
                    <EditorVisualTextField
                      label="Message"
                      fullWidth
                      name={props.input.name}
                      value={props.input.value}
                      onChange={props.input.onChange}
                    />
                  )}
                </DynamicField>
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </EntaAccordion>
  );
};

export default ValidationSection;
